import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LEVELS = ["iniciante", "intermediário", "avançado", "profissional"];
const ST = {
  pendente: { l: "Pendente", c: "text-sky-400 border-sky-500/40" },
  aprovado: { l: "Aprovado", c: "text-emerald-400 border-emerald-500/40" },
  reprovado: { l: "Reprovado", c: "text-red-400 border-red-500/40" },
  reavaliacao: { l: "Reenviar", c: "text-amber-400 border-amber-500/40" },
};

export default function AdminInstrumentVideos() {
  const [status, setStatus] = useState("pendente");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState({});

  const load = useCallback(() => api.get(`/admin/instrument-videos?status=${status}`)
    .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))), [status]);
  useEffect(() => { load(); }, [load]);

  const review = async (r, action) => {
    try { await api.post(`/admin/instrument-videos/${r.user_id}/${encodeURIComponent(r.instrument)}`, { action, note: note[`${r.user_id}-${r.instrument}`] || "" }); toast.success("Avaliação registrada."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setLevel = async (r, level) => {
    try { await api.post(`/admin/users/${r.user_id}/instrument-level`, { instrument: r.instrument, level }); toast.success("Nível atualizado."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-videos">
      <p className="text-sm text-zinc-400">Validação de vídeos por instrumento. Aprovar leva o instrumento de Iniciante para Intermediário (com perfil 100% completo). O nível pode ser definido manualmente.</p>
      <div className="flex gap-2 flex-wrap">
        {["pendente", "reavaliacao", "aprovado", "reprovado", "todos"].map((s) => (
          <button key={s} data-testid={`videos-filter-${s}`} onClick={() => setStatus(s)}
            className={`text-xs px-3 py-1.5 rounded-xl border capitalize ${status === s ? "border-[#FF4D00] text-[#FF4D00] bg-[#FF4D00]/10" : "border-white/10 text-zinc-400"}`}>{s}</button>
        ))}
      </div>
      {rows.length === 0 ? <p className="text-sm text-zinc-500 py-6">Nenhum vídeo neste filtro.</p> :
        rows.map((r) => {
          const key = `${r.user_id}-${r.instrument}`;
          const st = ST[r.status] || { l: r.status, c: "text-zinc-400 border-white/10" };
          return (
            <div key={key} data-testid={`video-row-${key}`} className="bg-card border border-white/[0.06] rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{r.stage_name}</span>
                <span className="text-xs text-zinc-500">{r.email}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-xl border border-accent/30 text-accent">{r.instrument}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-xl border ${st.c}`}>{st.l}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-xl border border-white/10 text-zinc-400 capitalize">Nível: {r.current_level}</span>
                {!r.profile_complete && <span className="text-[11px] px-2 py-0.5 rounded-xl border border-amber-500/40 text-amber-400">Perfil incompleto</span>}
              </div>
              <div className="space-y-1">
                {(r.urls || []).map((url, vi) => (
                  <a key={vi} href={url} target="_blank" rel="noreferrer" className="text-sm text-accent hover:underline break-all block">Vídeo {vi + 1}: {url}</a>
                ))}
                {(r.urls || []).length === 0 && <span className="text-xs text-zinc-600">Sem vídeos.</span>}
              </div>
              <Input value={note[key] || ""} onChange={(e) => setNote({ ...note, [key]: e.target.value })} placeholder="Observação (opcional, enviada ao músico)" className="bg-[#09090B] border-white/10 rounded-xl text-sm" />
              <div className="flex gap-2 flex-wrap items-center">
                <Button data-testid={`video-approve-${key}`} size="sm" onClick={() => review(r, "aprovar")} className="rounded-xl bg-emerald-600 hover:bg-emerald-700">Aprovar</Button>
                <Button data-testid={`video-reject-${key}`} size="sm" onClick={() => review(r, "reprovar")} variant="outline" className="rounded-xl bg-transparent border-red-500/40 text-red-400">Reprovar</Button>
                <Button data-testid={`video-reeval-${key}`} size="sm" onClick={() => review(r, "reavaliar")} variant="outline" className="rounded-xl bg-transparent border-amber-500/40 text-amber-400">Solicitar novo</Button>
                <select data-testid={`video-level-${key}`} onChange={(e) => e.target.value && setLevel(r, e.target.value)} defaultValue=""
                  className="text-xs bg-[#09090B] border border-white/15 rounded-xl px-2 py-1.5 text-zinc-200">
                  <option value="">Definir nível manual…</option>
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  <option value="auto">automático (sistema)</option>
                </select>
              </div>
            </div>
          );
        })}
    </div>
  );
}
